import { useCallback, useEffect, useRef, useState } from "react";
import { runLocalUpscale } from "@/lib/localUpscale";
import {
  loadImageElement,
  sleep,
  type ScaleOption,
  type UpscaleMode,
} from "@/lib/utils";

export type UpscaleStatus =
  | "idle"
  | "submitting"
  | "processing"
  | "succeeded"
  | "failed";

export interface UpscaleRequest {
  file: File;
  previewUrl: string;
  scale: ScaleOption;
  mode: UpscaleMode;
  faceEnhance: boolean;
}

export interface UpscaleState {
  status: UpscaleStatus;
  label: string;
  progress: number;
  error: string | null;
  resultUrl: string | null;
  resultBlob: Blob | null;
  elapsedMs: number;
  engine: "idle" | "cloud" | "local";
}

const INITIAL: UpscaleState = {
  status: "idle",
  label: "Standby",
  progress: 0,
  error: null,
  resultUrl: null,
  resultBlob: null,
  elapsedMs: 0,
  engine: "idle",
};

async function fileToDataUrlCapped(file: File, maxEdge = 1280): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const image = await loadImageElement(url);
    const w = image.naturalWidth;
    const h = image.naturalHeight;
    const edge = Math.max(w, h);
    if (edge <= maxEdge) {
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Canvas failed.");
      ctx.drawImage(image, 0, 0);
      return canvas.toDataURL("image/jpeg", 0.92);
    }
    const scale = maxEdge / edge;
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas failed.");
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.9);
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function useUpscale() {
  const [state, setState] = useState<UpscaleState>(INITIAL);
  const startedAt = useRef(0);
  const timer = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const resultUrlRef = useRef<string | null>(null);

  const clearTimers = () => {
    if (timer.current) window.clearInterval(timer.current);
    timer.current = null;
  };

  const revokeResult = () => {
    if (resultUrlRef.current) {
      URL.revokeObjectURL(resultUrlRef.current);
      resultUrlRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      clearTimers();
      abortRef.current?.abort();
      revokeResult();
    };
  }, []);

  const beginClock = () => {
    startedAt.current = performance.now();
    clearTimers();
    timer.current = window.setInterval(() => {
      setState((prev) => ({
        ...prev,
        elapsedMs: Math.round(performance.now() - startedAt.current),
      }));
    }, 120);
  };

  const finish = (blob: Blob, engine: "cloud" | "local") => {
    revokeResult();
    const objectUrl = URL.createObjectURL(blob);
    resultUrlRef.current = objectUrl;
    clearTimers();
    setState({
      status: "succeeded",
      label: engine === "cloud" ? "Cloud press done" : "Local press done",
      progress: 100,
      error: null,
      resultUrl: objectUrl,
      resultBlob: blob,
      elapsedMs: Math.round(performance.now() - startedAt.current),
      engine,
    });
  };

  const runCloud = async (
    file: File,
    scale: ScaleOption,
    mode: UpscaleMode,
    signal: AbortSignal,
  ) => {
    setState((prev) => ({
      ...prev,
      engine: "cloud",
      status: "submitting",
      label: "Queuing free cloud ESRGAN…",
      progress: 8,
    }));
    const image = await fileToDataUrlCapped(file);
    const started = await fetch("/api/upscale", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      signal,
      body: JSON.stringify({ image, scale, mode }),
    });
    if (!started.ok) throw new Error("Cloud queue refused.");
    const data = (await started.json()) as { predictionId?: string };
    if (!data.predictionId) throw new Error("No job id.");

    setState((prev) => ({
      ...prev,
      status: "processing",
      label: "Workers in the horde…",
      progress: 18,
    }));

    for (let i = 0; i < 40; i += 1) {
      if (signal.aborted) throw new DOMException("Aborted", "AbortError");
      const poll = await fetch(
        `/api/upscale?id=${encodeURIComponent(data.predictionId)}`,
        { signal },
      );
      if (!poll.ok) throw new Error("Poll failed.");
      const job = (await poll.json()) as {
        status: string;
        error?: string | null;
      };
      setState((prev) => ({
        ...prev,
        progress: Math.min(86, 18 + i * 2),
        label: `Cloud press ${job.status}…`,
      }));
      if (job.status === "succeeded") {
        const raw = await fetch(
          `/api/upscale?id=${encodeURIComponent(data.predictionId)}&raw=1`,
          { signal },
        );
        if (!raw.ok) throw new Error("Result missing.");
        const blob = await raw.blob();
        if (!blob.type.startsWith("image/")) throw new Error("Not an image.");
        finish(blob, "cloud");
        return;
      }
      if (job.status === "failed") throw new Error(job.error || "Cloud fault.");
      await sleep(1800);
    }
    throw new Error("Cloud queue timed out.");
  };

  const runLocal = async (request: UpscaleRequest, signal: AbortSignal) => {
    setState((prev) => ({
      ...prev,
      engine: "local",
      status: "processing",
      label: "Falling back to on-device ESRGAN…",
      progress: 12,
    }));
    const image = await loadImageElement(request.previewUrl);
    const blob = await runLocalUpscale({
      image,
      scale: request.scale,
      mode: request.mode,
      enhance: request.faceEnhance,
      signal,
      onProgress: (percent, label) => {
        setState((prev) => ({
          ...prev,
          status: "processing",
          label,
          progress: percent,
          engine: "local",
        }));
      },
    });
    finish(blob, "local");
  };

  const upscale = useCallback(async (request: UpscaleRequest) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    revokeResult();
    setState({ ...INITIAL, status: "submitting", label: "Booting press…" });
    beginClock();

    try {
      try {
        await runCloud(
          request.file,
          request.scale,
          request.mode,
          controller.signal,
        );
      } catch (cloudError) {
        if ((cloudError as Error).name === "AbortError") throw cloudError;
        await runLocal(request, controller.signal);
      }
    } catch (error) {
      if ((error as Error).name === "AbortError") return;
      clearTimers();
      setState((prev) => ({
        ...prev,
        status: "failed",
        label: "Press jammed",
        error: error instanceof Error ? error.message : "Failed.",
        elapsedMs: Math.round(performance.now() - startedAt.current),
      }));
      throw error;
    }
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    clearTimers();
    revokeResult();
    setState(INITIAL);
  }, []);

  return { ...state, upscale, reset };
}
